import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link, useLocation } from "react-router-dom";
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { CChartLine } from "@coreui/react-chartjs";
import { getStyle } from "@coreui/utils";
import { format, isValid, parseISO } from "date-fns";
import { ru } from "date-fns/locale/ru";
import GroupByToggle from "../UI/GroupByToggle";
import RestaurantGroupSelect from "../UI/RestaurantGroupSelect";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import {
  detailsAverageLabel,
  detailsBreakdownSubtitle,
  detailsBreakdownTitle,
  detailsRowColumnTitle,
} from "../../utils/iikoOlapGroup";
import datasStore from "../../store/DataStore";
import settingsStore from "../../store/SettingsStore";
import { logReportView } from "../../api/reportLogApi";

function chartAxisRange(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return {};
  const nums = arr.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  if (!nums.length) return {};
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min;
  const pad = span > 0 ? span * 0.12 : Math.max(1, Math.abs(max || 0) * 0.12);
  return { min: min - pad, max: max + pad };
}

function formatRowDate(iso, fallbackLabel) {
  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    try {
      const d = parseISO(iso);
      if (isValid(d)) return format(d, "d MMMM yyyy", { locale: ru });
    } catch {
    }
  }
  return fallbackLabel || "—";
}

function formatDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toNum(v) {
  if (v == null) return null;
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  if (typeof v === "string") {
    const s = v
      .replace(/\u00A0|\u202F/g, " ")
      .replace(/[%₽]/g, "")
      .replace(/\s+/g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isNaN(n) ? null : n;
  }
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function sumVal(nums) {
  const arr = Array.isArray(nums) ? nums : [];
  const x = arr.map(toNum).filter((n) => n != null);
  if (!x.length) return null;
  return x.reduce((a, b) => a + b, 0);
}

function avgVal(nums) {
  const arr = Array.isArray(nums) ? nums : [];
  const x = arr.map(toNum).filter((n) => n != null);
  if (!x.length) return null;
  return x.reduce((a, b) => a + b, 0) / x.length;
}

function lastVal(nums) {
  const arr = Array.isArray(nums) ? nums : [];
  if (!arr.length) return null;
  return toNum(arr[arr.length - 1]);
}

function fmtNumber(v, { decimals = 0 } = {}) {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const MetricDetailsPage = observer(
  ({ title, subtitle = "Детализация по дням", metricKey, unit = "", aggregate = "sum", color = "primary" }) => {
    const location = useLocation();
    const [selectedServer, setSelectedServer] = useState("");
    const [quickRange, setQuickRange] = useState("");

    const valuesRaw = datasStore?.[metricKey];
    const values = Array.isArray(valuesRaw) ? valuesRaw : [];
    const labelsShort = Array.isArray(datasStore.openDate) ? datasStore.openDate : [];
    const isoList = Array.isArray(datasStore.openDateIso) ? datasStore.openDateIso : [];

    const rows = useMemo(
      () =>
        values.map((v, i) => ({
          dateLabel: formatRowDate(isoList[i], labelsShort[i]),
          value: v,
          key: `${isoList[i] ?? i}-${i}`,
        })),
      [values, isoList, labelsShort],
    );

    const periodText =
      datasStore.fromDate && datasStore.toDate ? `${datasStore.fromDate} — ${datasStore.toDate}` : null;

    useEffect(() => {
      datasStore.clearError?.();
    }, []);

    useEffect(() => {
      const userId = localStorage.getItem("userId");
      logReportView({
        event: "report_view",
        reportKey: `details:${metricKey}`,
        reportTitle: title,
        userId,
        path: location.pathname,
        at: new Date().toISOString(),
      });
    }, []);

    useEffect(() => {
      const init = async () => {
        const userId = localStorage.getItem("userId");
        if (userId) await settingsStore.getServers(userId);
        if (!datasStore.fromDate || !datasStore.toDate) {
          const today = new Date();
          const cur = formatDate(today);
          const pastDate = new Date(today);
          pastDate.setDate(today.getDate() - 7);
          const past = formatDate(pastDate);
          datasStore.setFromDate(past);
          datasStore.setToDate(cur);
        }
      };
      init();
    }, []);

    useEffect(() => {
      if (!settingsStore.servers?.length) return;
      if (selectedServer !== "") return;
      const fromNav = location.state?.serverId;
      if (fromNav != null && settingsStore.servers.some((s) => String(s.id) === String(fromNav))) {
        setSelectedServer(String(fromNav));
      } else {
        setSelectedServer(String(settingsStore.servers[0].id));
      }
    }, [settingsStore.servers, selectedServer, location.state]);

    useEffect(() => {
      if (!selectedServer || !datasStore.fromDate || !datasStore.toDate) return;
      datasStore.loadPeriodData(selectedServer, datasStore.fromDate, datasStore.toDate);

      const userId = localStorage.getItem("userId");
      logReportView({
        event: "report_context",
        reportKey: `details:${metricKey}`,
        reportTitle: title,
        userId,
        serverId: selectedServer,
        dateFrom: datasStore.fromDate,
        dateTo: datasStore.toDate,
        path: location.pathname,
        at: new Date().toISOString(),
      });
    }, [selectedServer, datasStore.groupByRestaurant]);

    const handleGroupByChange = (byRestaurant) => {
      datasStore.setGroupByRestaurant(byRestaurant);
      if (!selectedServer || !datasStore.fromDate || !datasStore.toDate) return;
      datasStore.loadPeriodData(selectedServer, datasStore.fromDate, datasStore.toDate);
    };

    const handleRestaurantGroupChange = (groupName) => {
      datasStore.setSelectedRestaurantGroup(groupName);
      if (!selectedServer || !datasStore.fromDate || !datasStore.toDate) return;
      datasStore.loadPeriodData(selectedServer, datasStore.fromDate, datasStore.toDate);
    };

    const handleRefresh = (from, to) => {
      if (!selectedServer) return;
      datasStore.loadPeriodData(selectedServer, from, to);

      const userId = localStorage.getItem("userId");
      logReportView({
        event: "report_context",
        reportKey: `details:${metricKey}`,
        reportTitle: title,
        userId,
        serverId: selectedServer,
        dateFrom: from,
        dateTo: to,
        path: location.pathname,
        at: new Date().toISOString(),
      });
    };

    const handleServerChange = (e) => setSelectedServer(e.target.value);

    const unitText = unit ? ` ${unit}` : "";
    const decimals = unit === "%" ? 2 : 0;
    const sumValue = sumVal(values);
    const avgValue = avgVal(values);
    const lastValue = lastVal(values);

    return (
      <CContainer className="px-4 pb-5" style={{ marginTop: "100px" }}>
        <div className="mb-3">
          <Link to="/dashboard" className="text-decoration-none">
            ← К дашборду
          </Link>
        </div>

        <CCard className="mb-4">
          <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <strong>{title}</strong>
              <div className="small text-body-secondary fw-normal">
                {datasStore.groupByRestaurant
                  ? detailsBreakdownSubtitle(true)
                  : subtitle || detailsBreakdownSubtitle(false)}
              </div>
            </div>
            {periodText && (
              <CBadge color="secondary" className="text-wrap text-start">
                Период: {periodText}
              </CBadge>
            )}
          </CCardHeader>
          <CCardBody>
            <div className="mb-4 pb-3 border-bottom">
              <CRow className="g-3 mb-3">
                <CCol xs={12} md={6}>
                  <ServerSelect
                    servers={settingsStore.servers ?? []}
                    value={selectedServer}
                    onChange={handleServerChange}
                    id={`detailsServerSelect-${metricKey}`}
                    label="Сервер"
                    className="mb-0"
                  />
                </CCol>
                <CCol xs={12} md={6} lg={4}>
                  <GroupByToggle
                    value={datasStore.groupByRestaurant}
                    onChange={handleGroupByChange}
                    disabled={!selectedServer || datasStore.loading}
                    id={`groupByToggle-${metricKey}`}
                  />
                </CCol>
                {datasStore.groupByRestaurant && datasStore.restaurantGroups.length > 0 && (
                  <CCol xs={12} md={6} lg={4}>
                    <RestaurantGroupSelect
                      groups={datasStore.restaurantGroups}
                      value={datasStore.selectedRestaurantGroup}
                      onChange={handleRestaurantGroupChange}
                      disabled={!selectedServer || datasStore.loading}
                      id={`restaurantGroup-${metricKey}`}
                    />
                  </CCol>
                )}
              </CRow>

              <PeriodFilters
                fromDate={datasStore.fromDate}
                toDate={datasStore.toDate}
                onFromChange={(d) => datasStore.setFromDate(d)}
                onToChange={(d) => datasStore.setToDate(d)}
                onRefresh={handleRefresh}
                quickRange={quickRange}
                onQuickRangeChange={setQuickRange}
                disabled={!selectedServer || datasStore.loading}
                loading={datasStore.loading}
              />
            </div>

            <CRow className="g-3 mb-4">
              <CCol xs={12} sm={4}>
                <div className="text-body-secondary small">Сумма за период</div>
                <div className="fs-4 fw-semibold">
                  {fmtNumber(sumValue, { decimals })}
                  {unitText}
                </div>
              </CCol>
              <CCol xs={12} sm={4}>
                <div className="text-body-secondary small">
                  {detailsAverageLabel(datasStore.groupByRestaurant)}
                </div>
                <div className="fs-5 fw-semibold">
                  {fmtNumber(avgValue, { decimals })}
                  {unitText}
                </div>
              </CCol>
              <CCol xs={12} sm={4}>
                <div className="text-body-secondary small">Точек на графике</div>
                <div className="fs-5 fw-semibold">{values.length}</div>
                {lastValue != null && (
                  <div className="small text-body-secondary">
                    Последнее: {fmtNumber(lastValue, { decimals })}
                    {unitText}
                  </div>
                )}
              </CCol>
            </CRow>

            {datasStore.loading && values.length === 0 ? (
              <div className="d-flex justify-content-center align-items-center py-5 text-body-secondary">
                <CSpinner className="me-2" />
                Загрузка графика…
              </div>
            ) : values.length === 0 ? (
              <p className="text-body-secondary mb-0">Нет данных за выбранный период. Смените даты или нажмите «Обновить».</p>
            ) : (
              <>
                <div className="mb-4" style={{ height: "min(420px, 55vh)" }}>
                  <CChartLine
                    style={{ height: "100%", maxHeight: "420px" }}
                    data={{
                      labels: rows.map((r) => r.dateLabel),
                      datasets: [
                        {
                          label: `${title}${unit ? `, ${unit}` : ""}`,
                          backgroundColor:
                            color === "info"
                              ? "rgba(13, 202, 240, 0.15)"
                              : color === "danger"
                                ? "rgba(220, 53, 69, 0.12)"
                                : "rgba(13, 110, 253, 0.12)",
                          borderColor: getStyle(`--cui-${color}`) ?? "#0d6efd",
                          pointBackgroundColor: getStyle(`--cui-${color}`) ?? "#0d6efd",
                          borderWidth: 2,
                          fill: true,
                          tension: 0.35,
                          data: values.map((v) => toNum(v) ?? 0),
                        },
                      ],
                    }}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      interaction: { mode: "index", intersect: false },
                      plugins: {
                        legend: { display: true, position: "top" },
                        tooltip: {
                          callbacks: {
                            label(ctx) {
                              const n = ctx.parsed?.y;
                              if (n == null || Number.isNaN(n)) return "";
                              return ` ${fmtNumber(n, { decimals })}${unitText}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          ticks: { maxRotation: 45, minRotation: 0, autoSkip: true, maxTicksLimit: 14 },
                        },
                        y: {
                          beginAtZero: false,
                          ...chartAxisRange(values),
                          ticks: { callback: (val) => `${fmtNumber(val, { decimals })}` },
                        },
                      },
                    }}
                  />
                </div>

                <h6 className="mb-3">{detailsBreakdownTitle(datasStore.groupByRestaurant)}</h6>
                <div className="table-responsive">
                  <CTable striped hover className="align-middle">
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell scope="col">{detailsRowColumnTitle()}</CTableHeaderCell>
                        <CTableHeaderCell scope="col" className="text-end">
                          Значение{unit ? `, ${unit}` : ""}
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {[...rows].reverse().map((row) => (
                        <CTableRow key={row.key}>
                          <CTableDataCell>{row.dateLabel}</CTableDataCell>
                          <CTableDataCell className="text-end fw-medium">
                            {fmtNumber(row.value, { decimals })}
                            {unitText}
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              </>
            )}
          </CCardBody>
        </CCard>
      </CContainer>
    );
  },
);

export default MetricDetailsPage;

